import React, { forwardRef, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Down16 } from '@hagerty/icons';
import { useOnClickOutside } from '../../hooks';
import { StandardProps } from '../../types/common';
import { CloseIcon } from '../Icons';
import styles from './Menu.module.scss';
import { MenuLink } from './MenuLink';

const WAIT_TIME = 300; // ms

interface SubMenuProps extends StandardProps {
  /**
   * Links to display in the menu. Typically a list of MenuLink components.
   */
  link?: React.ReactNode;

  /**
   * Determines whether the link is active
   */
  isActive?: boolean;

  /**
   * Determines whether the menu is open
   */
  isOpen: boolean;

  /**
   * Determines whether the menu is the first child of the parent menu
   */
  isFirstChild?: boolean;

  /**
   * Determines whether to show the dropdown indicator
   */
  showDropdownIndicator?: boolean;

  /**
   * Index of the menu item
   */
  index: number;

  /**
   * Callback function to call when the menu is opened
   */
  handleOpen: (index: number) => void;

  /**
   * Callback function to call when the menu is closed
   */
  handleClose: () => void;
}

export const SubMenu = forwardRef<HTMLLIElement, SubMenuProps>(
  (
    {
      children,
      className,
      link,
      isActive,
      isOpen,
      isFirstChild,
      showDropdownIndicator = true,
      index,
      handleOpen,
      handleClose,
    },
    ref
  ) => {
    const isMenuLink = Boolean(isFirstChild && !children);
    const isMenuDropdown = Boolean(isFirstChild && children);
    const isMenuDropdownItem = Boolean(!isFirstChild);

    const buttonRef = useRef(null);
    const [wrapperRef, setWrapperRef] = useState<HTMLDivElement | null>(null);

    const [shouldWait, setShouldWait] = useState(false);

    const handleShouldWait = () => {
      setShouldWait(true);
      setTimeout(() => {
        setShouldWait(false);
      }, WAIT_TIME);
    };

    const handleExpandMenu = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      const element = e.target as HTMLElement;
      const sibling = element.nextElementSibling;
      if (sibling) {
        const submenu = sibling.querySelector('ul');
        if (submenu) {
          const parentMenuHeight =
            element?.parentElement?.parentElement?.offsetHeight || 0;
          const childMenuHeight = submenu.offsetHeight;
          if (
            childMenuHeight > parentMenuHeight &&
            element?.parentElement?.parentElement
          ) {
            element.parentElement.parentElement.style.height =
              childMenuHeight + 'px';
          }
        }
      }
    };

    const handleShrinkMenu = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      const element = e.target as HTMLElement;
      element?.parentElement?.parentElement?.removeAttribute('style');
    };

    const handleMouseEnter = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (!shouldWait) {
        if (!isOpen) {
          handleShouldWait();
          handleOpen(index);
        }
        if (!isFirstChild) {
          handleExpandMenu(e);
        }
      }
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      handleClose();
      if (!isFirstChild) {
        handleShrinkMenu(e);
      }
    };

    const handleClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (!shouldWait) {
        handleShouldWait();
        if (isOpen) {
          handleClose();
        } else {
          handleOpen(index);
          if (!isFirstChild) {
            handleExpandMenu(e);
          }
        }
        if (!isFirstChild) {
          handleShrinkMenu(e);
        }
      }
    };

    useOnClickOutside({ current: wrapperRef }, handleClose, 'wrapper');

    useEffect(() => {
      const handleEsc = (e: KeyboardEvent) => {
        e.key === 'Escape' && handleClose();
      };
      document?.addEventListener('keydown', handleEsc);
      return () => {
        document?.removeEventListener('keydown', handleEsc);
      };
    }, [handleClose]);

    return (
      // This ESLint rule is disabled because the mouse events are needed for the dropdown menu,
      // and the keyboard events are handled via the button itself. This is due to the way the styling
      // for the dropdown menu is handled. The mouse event is on the li, and the keyboard event is on the button.

      // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
      <li
        ref={ref}
        className={clsx(
          {
            [styles['hds-menu__item']]: isMenuDropdown || isMenuLink,
            [styles['has-dropdown']]: isMenuDropdown,
            [styles['hds-menu__dropdown-item']]: isMenuDropdownItem,
            [styles['has-flyout']]: isMenuDropdownItem,
            [styles['is-open']]: isOpen,
          },
          className
        )}
        onMouseOver={isMenuDropdownItem ? handleMouseEnter : undefined}
        onMouseLeave={isMenuDropdownItem ? handleMouseLeave : undefined}
        data-testid={`sub-menu-${index}`}
      >
        {children ? (
          <button
            type='button'
            ref={buttonRef}
            onClick={handleClick}
            className={clsx('wrapper', {
              [styles['hds-menu__link']]: isMenuDropdown,
              [styles['hds-menu__dropdown-link']]: !isMenuDropdown,
              [styles['is-active']]: isActive,
              [styles['is-open']]: isOpen,
            })}
            aria-haspopup={true}
            aria-expanded={isOpen}
            data-testid={`sub-menu-trigger-${index}`}
          >
            {isMenuDropdown ? (
              <>
                {link}
                {showDropdownIndicator && (
                  <Down16
                    className={clsx({
                      [styles['is-open']]: isOpen,
                    })}
                  />
                )}
              </>
            ) : (
              <>
                <span>{link}</span>
                <span className={styles['hds-menu__dropdown-icon']}></span>
              </>
            )}
          </button>
        ) : (
          <MenuLink isActive={isActive}>{link}</MenuLink>
        )}
        {children && (
          <div
            className={clsx('wrapper', styles['hds-menu__dropdown-wrapper'])}
            ref={setWrapperRef}
          >
            {isMenuDropdown && (
              <button
                type='button'
                className={styles['hds-menu__dropdown_close']}
                onClick={handleClose}
              >
                <CloseIcon className={styles.icon} />
              </button>
            )}
            <ul className={styles['hds-menu__dropdown']}>{children}</ul>
          </div>
        )}
      </li>
    );
  }
);
